import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonInput, IonItem, IonLabel, IonList, IonRadio, IonRadioGroup } from '@ionic/angular/standalone';
import { takeUntil } from 'rxjs/operators';
import { DestroyService } from 'src/app/services/destroy.service';
import { Status } from '../status';

interface InputElementAccess { getInputElement: () => Promise<HTMLElement> }

@Component({
  selector: 'app-ssqw-radio-form',
  templateUrl: './ssqw-radio-form.component.html',
  styleUrls: ['./ssqw-radio-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    IonList,
    IonRadioGroup,
    IonItem,
    IonLabel,
    IonRadio,
    IonInput
  ]
})
export class SsqwRadioFormComponent implements OnInit {
  public radioOptions: { label: string; value: string }[];
  @Input()
  public set options(value: string[]) {
    this.radioOptions = value.map((v, index) => ({
      label: v,
      value: index.toString()
    }));
  }
  @Input() allowOther: boolean;
  @Input() initial: { label: string; value: string; otherContent?: string };
  @Input() required: boolean;
  @Input() clearEvent: EventEmitter<void>;
  @Output() status = new EventEmitter<Status>();
  @Output() inputClicked = new EventEmitter<HTMLElement>();

  @ViewChild('radio', { static: true }) radioGroup: IonRadioGroup;
  @ViewChild('input', { static: true }) otherInput: IonInput;

  field = new FormControl('', [Validators.required]);

  constructor(private destroy$: DestroyService) {}

  ngOnInit(): void {
    this.clearEvent.pipe(takeUntil(this.destroy$)).subscribe(_ => this.field.setValue(''));

    if (this.allowOther) {
      this.radioOptions.push({ label: 'Other', value: 'other' });
    }

    this.radioGroup.ionChange.pipe(takeUntil(this.destroy$)).subscribe((s) => {
      this.status.emit(this.calculateStatus());
      if (this.showOther) {
        (this.otherInput as InputElementAccess).getInputElement().then((focusElement) => {
          setTimeout(() => focusElement.focus(), 0);
          this.inputClicked.emit(focusElement);
        });
      }
    });
    this.field.statusChanges.pipe(takeUntil(this.destroy$)).subscribe((s) => {
      this.status.emit(this.calculateStatus());
    });

    this.field.addValidators(Validators.pattern(/^(?!\s+$).*/));
    this.radioGroup.value = this.initial?.value;
    this.field.setValue(this.initial?.otherContent);

    this.clearEvent.pipe(takeUntil(this.destroy$)).subscribe((_) => {
      this.radioGroup.value = null;
      this.field.setValue('');
    });
  }

  get showOther(): boolean {
    return this.radioGroup.value === 'other';
  }

  private calculateStatus(): Status {
    if (!this.radioGroup.value) {
      return { valid: !this.required, errors: this.required ? { select: true } : null, value: null };
    }
    if (this.radioGroup.value === 'other') {
      return {
        valid: this.field.valid,
        errors: this.field.errors,
        value: { label: 'Other', value: 'other', otherContent: this.field.valid ? this.field.value : null }
      };
    } else {
      return {
        valid: true,
        errors: null,
        value: {
          label: this.radioOptions.find(ro => ro.value === this.radioGroup.value).label,
          value: this.radioGroup.value
        }
      };
    }
  }

  get length(): number {
    return this.field.value?.length ?? 0;
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).blur();
    }
  }

  onClick(event: MouseEvent): void {
    this.inputClicked.emit(event.target as HTMLElement);
  }
}
